import * as React from 'react'
import { Link, useParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { ArrowLeft } from 'lucide-react'
import { isRecordedPoint, type SiteBoundary } from '@lib/dto/geo.dto'
import type { SiteMapAsOfDto } from '@lib/dto/site-history.dto'
import { siteVisitService } from '@/services/SiteVisitService'
import { siteVisitKeys } from '@/hooks/queryKeys'
import { useSite } from '@/hooks/useSite'
import { createSiteMap } from '@/map/basemap'
import {
  detailForZoom,
  treeMarkerHtml,
  type MarkerDetail,
} from '@/field-map/TreeMarker'
import {
  MARKER_SIZE_PX,
  irrigationMarkerHtml,
  type IrrigationMarkerTone,
} from '@/irrigation-survey/IrrigationMarker'
import { equipmentGlyph } from '@/irrigation-survey/irrigationLabels'
import { formatVisitDay, visitCovers } from '@/site-visits/VisitBar'

/** An outline's corners, as Leaflet takes them. */
function corners(boundary: SiteBoundary): L.LatLng[] {
  return boundary.vertices.map((vertex) =>
    L.latLng(vertex.latitude, vertex.longitude),
  )
}

/** One irrigation pin, centered on its point as the survey draws it. */
function irrigationPin(
  latitude: number,
  longitude: number,
  tone: IrrigationMarkerTone,
  glyph?: string,
): L.Marker {
  return L.marker(L.latLng(latitude, longitude), {
    interactive: false,
    icon: L.divIcon({
      html: irrigationMarkerHtml({
        tone,
        ...(glyph === undefined ? {} : { glyph }),
        selected: false,
        pending: false,
        hasOpenProblem: false,
      }),
      className: '',
      iconSize: [MARKER_SIZE_PX, MARKER_SIZE_PX],
      iconAnchor: [MARKER_SIZE_PX / 2, MARKER_SIZE_PX / 2],
    }),
  })
}

/** Draw the site as it stood: outlines first, then the pins over them. */
function draw(
  group: L.LayerGroup,
  site: SiteMapAsOfDto,
  detail: MarkerDetail,
): void {
  group.clearLayers()
  if (site.boundary !== null) {
    L.polygon(corners(site.boundary), {
      className: 'stroke-map-halo',
      weight: 2,
      fill: false,
      dashArray: '6 4',
      interactive: false,
    }).addTo(group)
  }
  for (const area of site.areas) {
    L.polygon(corners(area.boundary), {
      className: 'stroke-map-halo',
      weight: 1,
      fill: false,
      opacity: 0.7,
      interactive: false,
    }).addTo(group)
  }
  for (const zone of site.zones) {
    if (zone.boundary === null) continue
    L.polygon(corners(zone.boundary), {
      className: 'stroke-map-teal',
      weight: 2,
      fill: false,
      dashArray: '4 3',
      interactive: false,
    }).addTo(group)
  }
  for (const source of site.waterSources) {
    if (!isRecordedPoint(source)) continue
    irrigationPin(source.latitude, source.longitude, 'waterSource').addTo(group)
  }
  for (const timer of site.timers) {
    if (!isRecordedPoint(timer)) continue
    irrigationPin(timer.latitude, timer.longitude, 'timer').addTo(group)
  }
  // Well components stand at their well and what is in a valve box at the
  // box, as on the survey.
  for (const item of site.equipment) {
    if (item.kind === 'WELL_COMPONENT' || item.boxId !== null) continue
    if (!isRecordedPoint(item)) continue
    irrigationPin(
      item.latitude,
      item.longitude,
      item.kind === 'VALVE_BOX' ? 'valveBox' : 'equipment',
      equipmentGlyph(item, site),
    ).addTo(group)
  }
  for (const tree of site.trees) {
    if (!isRecordedPoint(tree)) continue
    L.marker(L.latLng(tree.latitude, tree.longitude), {
      interactive: false,
      icon: L.divIcon({
        html: treeMarkerHtml({
          tree,
          detail,
          selected: false,
          moving: false,
          outsideBoundary: false,
          dimmed: false,
        }),
        className: '',
        iconSize: [0, 0],
        iconAnchor: [0, 0],
      }),
    }).addTo(group)
  }
}

/** Every point and corner drawn, to frame the map on. */
function extent(site: SiteMapAsOfDto): L.LatLng[] {
  if (site.boundary !== null) return corners(site.boundary)
  return [
    ...site.trees,
    ...site.waterSources,
    ...site.timers,
    ...site.equipment,
  ]
    .filter(isRecordedPoint)
    .map((point) => L.latLng(point.latitude, point.longitude))
}

/**
 * A site as it stood on a past visit's day: its outline, trees and
 * irrigation as they were, and the visit's findings on each tree's pin.
 * Read-only: the site record is only ever changed as it stands now. See
 * docs/site-visits.md, "The map as of a visit".
 */
export function SiteMapAsOfPage() {
  const { siteId = '', visitId = '' } = useParams()
  const site = useSite(siteId)
  const query = useQuery({
    queryKey: siteVisitKeys.map(siteId, visitId),
    queryFn: () => siteVisitService.mapAsOf(visitId),
    enabled: visitId !== '',
    retry: false,
  })
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const mapRef = React.useRef<L.Map | null>(null)
  const groupRef = React.useRef<L.LayerGroup | null>(null)
  const framedRef = React.useRef(false)
  const [zoom, setZoom] = React.useState<number | null>(null)

  React.useEffect(() => {
    const container = containerRef.current
    if (container === null) return undefined
    const map = createSiteMap(container)
    mapRef.current = map
    groupRef.current = L.layerGroup().addTo(map)
    map.on('zoomend', () => {
      setZoom(map.getZoom())
    })
    return () => {
      map.remove()
      mapRef.current = null
      groupRef.current = null
    }
  }, [])

  const data = query.data
  React.useEffect(() => {
    const map = mapRef.current
    const group = groupRef.current
    if (map === null || group === null || data === undefined) return
    if (!framedRef.current) {
      const points = extent(data)
      if (points.length > 0) {
        map.fitBounds(L.latLngBounds(points), {
          padding: [48, 48],
          maxZoom: 20,
        })
      }
      framedRef.current = true
    }
    draw(group, data, detailForZoom(zoom ?? map.getZoom()))
  }, [data, zoom])

  const backTo =
    site.data === undefined ? '/clients' : `/clients/${site.data.clientId}`

  function renderNotice(): React.ReactNode {
    if (query.isPending) {
      return <p className="text-xs text-muted-text">Loading the map…</p>
    }
    if (query.isError) {
      return (
        <p role="alert" className="text-xs text-danger">
          This visit’s map could not be loaded.
        </p>
      )
    }
    return (
      <p className="text-xs text-muted-text">
        As of {formatVisitDay(query.data.asOf)} ·{' '}
        {visitCovers(query.data.visit)} · Read-only
      </p>
    )
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-app">
      <header className="flex items-center gap-2 border-b border-default bg-surface px-3 py-2">
        <Link
          to={backTo}
          className="rounded p-1.5 text-secondary-text hover:bg-subtle"
          aria-label="Back to client"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">
            {site.data?.name ?? 'Site'}
          </p>
          {renderNotice()}
        </div>
      </header>
      <div className="relative min-h-0 flex-1">
        <div ref={containerRef} className="absolute inset-0" />
      </div>
    </div>
  )
}
